import { get } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismViewSwitcher } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type { ComparisonSet } from 'frontend-utils/utils/constants/benchmark-insight';
import {
  type HeroChartAxisOption,
  type Kpi,
  KPI_INDEX_LONG_AND_ABBREVIATED_TRANSLATIONS,
  KPI_INDEX_TRANSLATIONS,
  KPI_INDEXES_FOR_KPIS,
  KPI_INDEXOPTION,
  KPI_LONG_TRANSLATIONS,
  KPIS,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { DateRangeType } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';

import styles from './hero-chart-axis-buttons.module.css';

interface HotelBenchmarkInsightHeroChartAxisButtonsSignature {
  Args: {
    changeSelectedAxisOption: (axisOption: HeroChartAxisOption) => void;
    changeSelectedKpi: (kpi: Kpi) => void;
    kpiDiffVsComparisonSetTranslation: string;
    oppositeKpi: Kpi;
    primaryComparisonSet: ComparisonSet;
    selectedAxisOption: HeroChartAxisOption;
    selectedKpi: Kpi;
    selectedRange: DateRangeType;
  };
}

export default class HotelBenchmarkInsightHeroChartAxisButtons extends Component<HotelBenchmarkInsightHeroChartAxisButtonsSignature> {
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];

  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked previousSelectedAxisOption: HeroChartAxisOption =
    KPI_INDEXOPTION.KPI_DIFFERENCE;

  get disabledButtonTooltipText(): string {
    if (!this.benchmarkInsightFeatures.hasOwnPmsIntegration) {
      return this.intl.t(
        'benchmark_insight.trial_without_data.chart_legend_tooltip',
      );
    }

    if (this.isKpiDifferenceButtonDisabled) {
      return this.intl.t(
        'benchmark_insight.hidden_future_data.button_tooltip.adr_difference',
      );
    }

    if (this.isIndexButtonDisabled && this.args.selectedKpi === KPIS.ADR) {
      return this.intl.t(
        'benchmark_insight.hidden_future_data.button_tooltip.ari',
      );
    }

    return this.intl.t(
      'benchmark_insight.hidden_future_data.button_tooltip.rgi',
    );
  }

  get indexTranslationForSelectedKpi(): string {
    return this.intl.t(
      KPI_INDEX_TRANSLATIONS[KPI_INDEXES_FOR_KPIS[this.args.selectedKpi]],
    );
  }

  get isIndexButtonDisabled(): boolean {
    const { primaryComparisonSet, selectedRange, selectedKpi } = this.args;

    const isHiddenFutureCompsetData =
      this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
        primaryComparisonSet,
        selectedRange.start,
        selectedKpi,
      );

    return (
      isHiddenFutureCompsetData ||
      !this.benchmarkInsightFeatures.hasOwnPmsIntegration
    );
  }

  get isKpiDifferenceButtonDisabled(): boolean {
    const { primaryComparisonSet, selectedRange, oppositeKpi } = this.args;

    const isHiddenFutureCompsetData =
      this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
        primaryComparisonSet,
        selectedRange.start,
        oppositeKpi,
      );

    return (
      isHiddenFutureCompsetData ||
      !this.benchmarkInsightFeatures.hasOwnPmsIntegration
    );
  }

  get isKpiDifferenceButtonVisible(): boolean {
    return this.args.selectedKpi !== KPIS.REVPAR;
  }

  get kpiDifferenceTranslation(): string {
    return this.intl.t('benchmark_insight.kpi_difference', {
      kpi: this.intl.t(KPI_LONG_TRANSLATIONS[this.args.oppositeKpi]),
    });
  }

  get rightYAxisTitle(): string {
    if (this.args.selectedAxisOption === KPI_INDEXOPTION.KPI_DIFFERENCE) {
      return this.args.kpiDiffVsComparisonSetTranslation;
    }

    return this.intl.t(
      KPI_INDEX_LONG_AND_ABBREVIATED_TRANSLATIONS[
        KPI_INDEXES_FOR_KPIS[this.args.selectedKpi]
      ],
    );
  }

  getEnabledAxisOptionIfDisabled(
    axisOption: HeroChartAxisOption,
  ): HeroChartAxisOption {
    let newAxisOption = axisOption;

    // If the axis option button we want to switch to is disabled, we need to switch to the other one.
    // Only adjust it if the button is disabled, otherwise leave it as is.
    if (
      newAxisOption === KPI_INDEXOPTION.INDEX &&
      this.isIndexButtonDisabled &&
      this.isKpiDifferenceButtonVisible
    ) {
      newAxisOption = KPI_INDEXOPTION.KPI_DIFFERENCE;
    } else if (
      newAxisOption === KPI_INDEXOPTION.KPI_DIFFERENCE &&
      this.isKpiDifferenceButtonDisabled
    ) {
      newAxisOption = KPI_INDEXOPTION.INDEX;
    }

    return newAxisOption;
  }

  @action
  handleClickSelectedAxisOption(axisOption: string): void {
    if (axisOption === this.args.selectedAxisOption) {
      return;
    }

    this.updateSelectedAxisOptionAndPrevious(axisOption as HeroChartAxisOption);

    this.usageAnalytics.track('Secondary Chart Axis Changed', {
      eventTrigger: 'performance-overview',
      product: 'benchmark-insight',
      type: axisOption,
      primary: this.args.selectedKpi,
    });
  }

  @action
  updateSelectedAxisOptionAndPrevious(axisOption: HeroChartAxisOption): void {
    this.previousSelectedAxisOption = axisOption;
    this.args.changeSelectedAxisOption(axisOption);
  }

  @action
  updateSelectedKpiAndAxisOption(kpi: string): void {
    if (kpi === this.args.selectedKpi) {
      return;
    }

    this.args.changeSelectedKpi(kpi as Kpi);

    this.usageAnalytics.track('Primary Chart Axis Changed', {
      eventTrigger: 'performance-overview',
      product: 'benchmark-insight',
      type: kpi,
    });

    if (kpi === KPIS.REVPAR) {
      this.previousSelectedAxisOption = this.args.selectedAxisOption;
      this.args.changeSelectedAxisOption(KPI_INDEXOPTION.INDEX);
    } else {
      const newAxisOption = this.getEnabledAxisOptionIfDisabled(
        this.previousSelectedAxisOption,
      );
      this.updateSelectedAxisOptionAndPrevious(newAxisOption);
    }
  }

  <template>
    <div class="{{styles.container}} flex justify-between">
      <PrismViewSwitcher
        @activeButtonId={{@selectedKpi}}
        @onSetActiveButtonId={{this.updateSelectedKpiAndAxisOption}}
        as |ViewSwitcher|
      >
        <ViewSwitcher.button
          @id={{KPIS.OCCUPANCY}}
          @label={{t (get KPI_LONG_TRANSLATIONS KPIS.OCCUPANCY)}}
        />

        <ViewSwitcher.button
          @id={{KPIS.ADR}}
          @label={{t (get KPI_LONG_TRANSLATIONS KPIS.ADR)}}
        />

        <ViewSwitcher.button
          @id={{KPIS.REVPAR}}
          @label={{t (get KPI_LONG_TRANSLATIONS KPIS.REVPAR)}}
        />
      </PrismViewSwitcher>

      <div class={{styles.line}}></div>

      <div class="{{styles.axis-title}} text-200 flex justify-center">
        <div class={{styles.label}}>
          {{t (get KPI_LONG_TRANSLATIONS @selectedKpi)}}
        </div>
      </div>

      {{#if @primaryComparisonSet}}
        <PrismViewSwitcher
          @activeButtonId={{@selectedAxisOption}}
          @onSetActiveButtonId={{this.handleClickSelectedAxisOption}}
          as |ViewSwitcher|
        >
          {{#if this.isKpiDifferenceButtonVisible}}
            <ViewSwitcher.button
              @id={{KPI_INDEXOPTION.KPI_DIFFERENCE}}
              @isDisabled={{this.isKpiDifferenceButtonDisabled}}
              @label={{this.kpiDifferenceTranslation}}
              @tooltipText={{if
                this.isKpiDifferenceButtonDisabled
                this.disabledButtonTooltipText
              }}
            />
          {{/if}}

          <ViewSwitcher.button
            @id={{KPI_INDEXOPTION.INDEX}}
            @isDisabled={{this.isIndexButtonDisabled}}
            @label={{this.indexTranslationForSelectedKpi}}
            @tooltipText={{if
              this.isIndexButtonDisabled
              this.disabledButtonTooltipText
            }}
          />
        </PrismViewSwitcher>

        <div class={{styles.line-right}}></div>

        <div class="{{styles.axis-title-right}} text-200 flex justify-center">
          <div class={{styles.axis-title-right-label}}>
            {{this.rightYAxisTitle}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
